// Lightweight bridge for communicating with the extension background for auth state
import { ext, hasExtensionRuntime } from '../utils/browserApi';

export interface AuthUser {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
  providerId: string;
}

// Use the runtime ID of the current extension so this never breaks on rebuild or ID change.
const EXTENSION_ID: string =
  (ext as any)?.runtime?.id || 'bmggidclgpalmpkhceeeibcdfmbmcpda';

// Helper to extract a meaningful error message from various error types
function getErrorMessage(err: any): string {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err.message) return err.message;
  if (err.error) return String(err.error);
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

// Ping the service worker to wake it up if it's sleeping
async function wakeUpServiceWorker(): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error('Ping timeout')), 1000);
    const isExtCtx = hasExtensionRuntime();

    const done = (_res: any) => {
      clearTimeout(timeoutId);
      if (ext.runtime?.lastError) reject(ext.runtime.lastError);
      else resolve();
    };

    if (!isExtCtx) {
      if ((ext as any)?.runtime?.sendMessage) {
        ext.runtime.sendMessage(EXTENSION_ID, { type: 'ping' }, done);
      } else {
        clearTimeout(timeoutId);
        reject(new Error('Extension runtime not available'));
      }
    } else {
      ext.runtime.sendMessage({ type: 'ping' }, done);
    }
  });
}

async function sendMessage(message: any, retries = 3): Promise<any> {
  try {
    await wakeUpServiceWorker();
  } catch (pingErr) {
    console.warn('Failed to ping service worker, proceeding anyway:', pingErr);
  }
  return new Promise((resolve, reject) => {
    try {

      const isExtCtx = hasExtensionRuntime();

      const attemptSend = (attempt: number) => {
        const callback = (res: any) => {
          if (ext.runtime?.lastError) {
            const errorMsg = (ext.runtime.lastError as any).message || 'Unknown error';
            if (attempt < retries && errorMsg.includes('Receiving end does not exist')) {
              console.warn(`Message attempt ${attempt} failed, retrying...`, ext.runtime.lastError);
              setTimeout(() => attemptSend(attempt + 1), 200 * attempt);
            } else {
              reject(ext.runtime.lastError);
            }
          } else {
            resolve(res);
          }
        };

        if (!isExtCtx) {
          if ((ext as any)?.runtime?.sendMessage) {
            ext.runtime.sendMessage(EXTENSION_ID, message, callback);
          } else {
            reject(new Error('Extension runtime not available'));
          }
        } else {
          ext.runtime.sendMessage(message, callback);
        }
      };

      attemptSend(1);
    } catch (err) {
      reject(err);
    }
  });
}

export const getUser = async (): Promise<{ user: AuthUser | null; status: string }> => {
  try {
    const res = await sendMessage({ type: 'auth:getUser' });
    return res || { user: null, status: 'signed-out' };
  } catch {
    return { user: null, status: 'unknown' };
  }
};

export const openSignInUI = async (provider?: 'google' | 'apple'): Promise<{ success: boolean; error?: string }> => {
  try {
    const res = await sendMessage({ type: 'auth:openSignIn', provider });
    return res || { success: false };
  } catch (err) {
    console.error('Auth Bridge Error (openSignInUI):', err);
    return { success: false, error: getErrorMessage(err) };
  }
};

export const signOut = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const res = await sendMessage({ type: 'auth:signOut' });
    return res || { success: false };
  } catch (err) {
    return { success: false, error: getErrorMessage(err) };
  }
};

const bgAuthBridge = { getUser, openSignInUI, signOut };
export default bgAuthBridge;
